import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import axiosClient from "../../axios-client";

const defaultIndustries = [
  "Biotechnology",
  "Clinical Diagnostics",
  "Consumer Goods",
  "Polymers & Resins",
];

const menuItems = [
  {
    id: 0,
    name: "Home",
    defaultImage: "/images/home.png",
    activeImage: "/images/Home-pink.png",
    link: "/",
  },
  {
    id: 1,
    name: "Industries",
    defaultImage: "/images/industry.png",
    activeImage: "/images/Edit-Square-pink.png",
    link: "/listing",
    isDropdown: true,
  },
  {
    id: 2,
    name: "Companies",
    defaultImage: "/images/company.png",
    activeImage: "/images/pink_company.png",
    link: "/companies",
  },
  {
    id: 3,
    name: "ESG Consulting",
    defaultImage: "/images/man.png",
    activeImage: "/images/esg.png",
    link: "/esg-consulting",
  },
  {
    id: 4,
    name: "Consumer Insights",
    defaultImage: "/images/search_insights.png",
    activeImage: "/images/pink_insights.png",
    link: "/consumer-insights",
  },
  {
    id: 5,
    name: "Contact",
    defaultImage: "/images/contact.png",
    activeImage: "/images/pink_contact.png",
    link: "/contact-us",
  },
];

const Sidebar = ({ isExpanded, handleClick }) => {
  const [activeItem, setActiveItem] = useState(null);
  const [showList, setShowList] = useState(4);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sync active item and dropdown with current route
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === "/listing" || currentPath.startsWith("/industries")) {
      setActiveItem(1);
      setIsDropdownOpen(true);
    } else {
      const activeMenuItem = menuItems.find((item) => item.link === currentPath);
      if (activeMenuItem) {
        setActiveItem(activeMenuItem.id);
      } else {
        setActiveItem(null);
      }
    }
  }, [location.pathname]);

  const handleDropdownToggle = () => {
    setIsDropdownOpen((prev) => {
      const next = !prev;
      if (!next) {
        const currentPath = location.pathname;
        const activeMenuItem = menuItems.find((item) => item.link === currentPath);
        setActiveItem(activeMenuItem ? activeMenuItem.id : null);
      } else {
        setActiveItem(1);
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await axiosClient.get("/reports/getReport");
        if (response.data && Array.isArray(response.data)) {
          const categories = response.data
            .map((report) => report.category)
            .filter(Boolean);
          const unique = [...new Set(categories)];
          if (unique.length > 0) {
            setReports(unique);
          }
        }
      } catch (error) {
        console.error("Error fetching reports categories, using default industries:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  const displayCategories = reports.length > 0 ? reports : defaultIndustries;

  const handleIndustryClick = async (industry) => {
    try {
      const response = await axiosClient.get(
        `/reports/find-by-category/?category=${encodeURIComponent(industry)}`
      );
      navigate(`/listing?search=${encodeURIComponent(industry)}`, {
        state: { reports: response.data },
      });
    } catch (error) {
      console.error("Error fetching reports by category:", error);
      navigate(`/listing?search=${encodeURIComponent(industry)}`);
    }
  };

  return (
    <div
      className={`flex flex-col py-5 px-2 bg-white ${
        isExpanded ? "w-72" : "w-32"
      } transition-all duration-500 p-2`}
    >
      <div className="flex justify-between items-center gap-3 font-bold pb-5 relative ">
        <img
          src="/images/Bizwitlogo.svg"
          alt="Logo"
          className={`${
            !isExpanded ? "w-0" : "w-[95%]"
          } transition-all duration-300`}
        />
        <div className="flex overflow-hidden w-full">
          <img
            src="/images/icon.png"
            alt="Logo"
            className={`${
              !isExpanded ? "w-[32%]" : "hidden"
            } transition-all duration-500 ml-8`}
          />
          <button
            onClick={handleClick}
            style={{ marginLeft: isExpanded ? "0" : "-25px", color: "white" }}
            className={
              isExpanded
                ? "bg-pink hover:bg-pink-600 text-white p-2 rounded-md transition-colors duration-300 absolute top-5 right-2"
                : ""
            }
          >
            {isExpanded ? <FaChevronLeft /> : <FaChevronRight />}
          </button>
        </div>
      </div>
      <nav className="flex-1 p-2">
        {menuItems.map((item) => (
          <div key={item.id}>
            {item.isDropdown ? (
              /* Dropdown Trigger Item (Clicking icon, text or chevron toggles smoothly) */
              <div
                onClick={handleDropdownToggle}
                className={`flex ${
                  !isExpanded ? "flex-col" : "flex-row"
                } items-center py-4 px-4 cursor-pointer rounded-xl transition-all duration-300 select-none ${
                  activeItem === item.id ? "bg-pink-100" : "bg-light hover:bg-[#ffeaf1]"
                }`}
              >
                <img
                  src={
                    activeItem === item.id
                      ? item.activeImage
                      : item.defaultImage
                  }
                  alt={item.name}
                  className={`w-6 h-6 transition-transform duration-200 ${
                    isExpanded ? "mr-6" : "mb-2"
                  }`}
                />
                <span
                  className={`${
                    !isExpanded ? "text-xs text-center" : "block"
                  } font-poppins font1 font-medium transition-colors duration-200 ${
                    activeItem === item.id ? "text-[#f33f7d]" : "text-gray-800"
                  }`}
                >
                  {item.name}
                </span>
                {isExpanded && (
                  <span className="ml-auto flex items-center justify-center p-1">
                    <FaChevronDown
                      className={`text-sm text-[#f33f7d] transition-transform duration-300 ease-in-out ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                )}
              </div>
            ) : (
              /* Normal Navigation Link */
              <Link to={item.link}>
                <div
                  onClick={() => setActiveItem(item.id)}
                  className={`flex ${
                    !isExpanded ? "flex-col" : "flex-row"
                  } items-center py-4 px-4 cursor-pointer rounded-xl transition-all duration-300 ${
                    activeItem === item.id ? "bg-pink-100" : "bg-light hover:bg-[#ffeaf1]"
                  }`}
                >
                  <img
                    src={
                      activeItem === item.id
                        ? item.activeImage
                        : item.defaultImage
                    }
                    alt={item.name}
                    className={`w-6 h-6 transition-transform duration-200 ${
                      isExpanded ? "mr-6" : "mb-2"
                    }`}
                  />
                  <span
                    className={`${
                      !isExpanded ? "text-xs text-center" : "block"
                    } font-poppins font1 font-medium transition-colors duration-200 ${
                      activeItem === item.id ? "text-[#f33f7d]" : "text-gray-800"
                    }`}
                  >
                    {item.name}
                  </span>
                </div>
              </Link>
            )}

            {/* Smooth Animated Dropdown Content with Theme UI */}
            {item.isDropdown && isExpanded && (
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isDropdownOpen
                    ? "max-h-[380px] opacity-100 mt-2 ml-4 pl-3 border-l-2 border-pink-300"
                    : "max-h-0 opacity-0 pointer-events-none ml-4 pl-3"
                }`}
                style={{
                  overflowY: isDropdownOpen ? "auto" : "hidden",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {displayCategories.slice(0, showList).map((report, index) => {
                  const isCurrent =
                    location.search.includes(encodeURIComponent(report)) ||
                    location.search.includes(report);
                  return (
                    <div
                      key={`report-${index}`}
                      onClick={() => handleIndustryClick(report)}
                      className={`block py-2 px-3 my-1 text-sm font-poppins rounded-lg cursor-pointer transition-all duration-200 text-gray-700 hover:text-[#f33f7d] hover:bg-[#ffeaf1] hover:pl-4 ${
                        isCurrent
                          ? "bg-[#ffeaf1] text-[#f33f7d] font-semibold pl-4"
                          : ""
                      }`}
                    >
                      {report}
                    </div>
                  );
                })}
                <button
                  onClick={() => {
                    if (showList === 4 && displayCategories.length > 4) {
                      setShowList(displayCategories.length);
                    } else {
                      navigate("/listing");
                    }
                  }}
                  className="w-full text-left py-2 px-3 my-1 text-xs font-poppins font-semibold text-[#f33f7d] hover:bg-[#ffeaf1] rounded-lg transition-all duration-200 flex items-center justify-between"
                >
                  <span>
                    {showList === 4 && displayCategories.length > 4
                      ? "View More Industries"
                      : "Explore All Industries"}
                  </span>
                  <FaChevronRight className="text-[10px]" />
                </button>
              </div>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
};

export default Sidebar;
